import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation, Trans } from 'react-i18next'
import {
  ArrowLeft, ChevronRight, Search, CheckCircle2, AlertCircle,
  Eye, EyeOff, RefreshCw, Folder, FolderOpen, Code, Wand2,
  Shield, Copy, Check,
} from 'lucide-react'
import clsx from 'clsx'
import { adApi } from '../../services/adApi'
import { useCreateAdConfig } from '../../hooks/useAdDomains'

// ─── Árvore de OUs ────────────────────────────────────────────────────────────
function OuNode({ node, selected, onSelect, depth = 0 }) {
  const [open, setOpen] = useState(depth === 0)
  const hasChildren = node.children?.length > 0
  const isSelected = selected === node.dn

  return (
    <div>
      <div
        className={clsx(
          'flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer text-sm transition-colors',
          isSelected ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-100 text-gray-700',
        )}
        style={{ paddingLeft: `${8 + depth * 16}px` }}
        onClick={() => onSelect(node.dn)}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setOpen(!open) }}
            className="flex-shrink-0"
          >
            {open ? <FolderOpen className="w-4 h-4 text-yellow-500" /> : <Folder className="w-4 h-4 text-yellow-500" />}
          </button>
        ) : (
          <Folder className="w-4 h-4 text-gray-400 flex-shrink-0" />
        )}
        <span className="truncate">{node.name}</span>
        {isSelected && <CheckCircle2 className="w-3.5 h-3.5 text-blue-600 ml-auto flex-shrink-0" />}
      </div>
      {open && hasChildren && (
        <div>
          {node.children.map((child) => (
            <OuNode key={child.dn} node={child} selected={selected} onSelect={onSelect} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  )
}

export default function DomainWizard() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const createMutation = useCreateAdConfig()

  const STEPS = [
    { id: 'mode',        label: t('domains.wizard.stepMode')    },
    { id: 'connect',     label: t('domains.wizard.step1')       },
    { id: 'discover',    label: t('domains.wizard.stepDiscover')},
    { id: 'serviceacct', label: t('domains.wizard.stepService') },
    { id: 'confirm',     label: t('domains.wizard.step4')       },
  ]

  const [mode, setMode] = useState('wizard')
  const [step, setStep] = useState(0)
  const [showPwd, setShowPwd] = useState(false)

  const [wiz, setWiz] = useState({
    server: '', port: 389, useSsl: false,
    adminUsername: '', adminPassword: '',
  })
  const [discovering, setDiscovering] = useState(false)
  const [discovered, setDiscovered] = useState(null)
  const [discoverError, setDiscoverError] = useState(null)
  const [selectedOu, setSelectedOu] = useState(null)
  const [displayName, setDisplayName] = useState('')

  const [provisioningAccount, setProvisioningAccount] = useState(false)
  const [svcResult, setSvcResult] = useState(null)
  const [svcError, setSvcError] = useState(null)
  const [scriptCopied, setScriptCopied] = useState(false)

  const [adv, setAdv] = useState({
    displayName: '', server: '', port: 389, useSsl: false,
    baseDn: '', bindDn: '', bindPassword: '',
    userSearchFilter: '(sAMAccountName={0})', groupSearchBase: '', enabled: true,
  })

  const [saving, setSaving] = useState(false)

  const handleDiscover = async () => {
    setDiscoverError(null)
    setDiscovering(true)
    try {
      const result = await adApi.discover({
        server: wiz.server, port: wiz.port, useSsl: wiz.useSsl,
        adminUsername: wiz.adminUsername, adminPassword: wiz.adminPassword,
      })
      if (!result.success) {
        setDiscoverError(result.errorMessage || t('domains.wizard.discoverFailed'))
      } else {
        setDiscovered(result)
        setSelectedOu(result.baseDn)
        setDisplayName(result.dnsHostName?.split('.')[0]?.toUpperCase() ?? result.baseDn ?? '')
        setStep(2)
      }
    } catch (e) {
      setDiscoverError(e.response?.data?.error ?? e.message)
    } finally {
      setDiscovering(false)
    }
  }

  const handleProvisionServiceAccount = async () => {
    setSvcError(null)
    setSvcResult(null)
    setProvisioningAccount(true)
    try {
      const result = await adApi.provisionServiceAccount({
        server: wiz.server,
        port: wiz.port,
        useSsl: wiz.useSsl,
        baseDn: discovered.baseDn,
        adminUsername: wiz.adminUsername,
        adminPassword: wiz.adminPassword,
      })
      setSvcResult(result)
      setStep(3)
    } catch (e) {
      const err = e.response?.data?.error ?? e.message
      setSvcError(err)
    } finally {
      setProvisioningAccount(false)
    }
  }

  const handleCopyScript = (script) => {
    navigator.clipboard.writeText(script).then(() => {
      setScriptCopied(true)
      setTimeout(() => setScriptCopied(false), 2000)
    })
  }

  const handleCreateWizard = async () => {
    setSaving(true)
    try {
      const data = {
        displayName,
        server: wiz.server,
        port: wiz.port,
        useSsl: wiz.useSsl,
        baseDn: discovered.baseDn,
        bindDn: svcResult?.serviceAccountDn ?? wiz.adminUsername,
        bindPassword: svcResult?.passwordSet ? undefined : wiz.adminPassword,
        userSearchFilter: '(sAMAccountName={0})',
        groupSearchBase: selectedOu !== discovered.baseDn ? selectedOu : null,
        enabled: true,
      }
      const created = await createMutation.mutateAsync(data)
      navigate(`/domains/${created.id}`)
    } catch (e) {
      alert(t('domains.wizard.createError') + e.message)
    } finally {
      setSaving(false)
    }
  }

  const handleCreateAdvanced = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      const created = await createMutation.mutateAsync(adv)
      navigate(`/domains/${created.id}`)
    } catch (e) {
      alert(t('domains.wizard.createError') + e.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/domains')} className="btn btn-secondary text-sm flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" />
          {t('common.buttons.back')}
        </button>
        <ChevronRight className="w-4 h-4 text-gray-400" />
        <span className="text-sm text-gray-500">{t('domains.title')}</span>
        <ChevronRight className="w-4 h-4 text-gray-400" />
        <span className="text-sm font-medium text-gray-800">{t('domains.wizard.breadcrumb')}</span>
      </div>

      <h1 className="text-2xl font-bold text-gray-900">{t('domains.wizard.pageTitle')}</h1>

      <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-xl w-fit">
        <button
          onClick={() => setMode('wizard')}
          className={clsx('flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-colors', mode === 'wizard' ? 'bg-white shadow text-gray-900' : 'text-gray-500')}
        >
          <Wand2 className="w-4 h-4" />
          {t('domains.wizard.modeWizard')}
        </button>
        <button
          onClick={() => setMode('advanced')}
          className={clsx('flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-colors', mode === 'advanced' ? 'bg-white shadow text-gray-900' : 'text-gray-500')}
        >
          <Code className="w-4 h-4" />
          {t('domains.wizard.modeAdvanced')}
        </button>
      </div>

      {mode === 'wizard' ? (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            {STEPS.map((s, i) => (
              <div key={s.id} className="flex items-center gap-2">
                <div className={clsx(
                  'w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold',
                  i < step ? 'bg-green-500 text-white' : i === step ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
                )}>
                  {i < step ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                </div>
                <span className={clsx('text-sm', i === step ? 'font-medium text-gray-900' : 'text-gray-400')}>{s.label}</span>
                {i < STEPS.length - 1 && <ChevronRight className="w-4 h-4 text-gray-300" />}
              </div>
            ))}
          </div>

          {step === 0 && (
            <div className="card p-6 space-y-4">
              <p className="text-sm text-gray-600">
                {t('domains.wizard.intro')}
              </p>
              <button onClick={() => setStep(1)} className="btn btn-primary">{t('domains.wizard.startButton')}</button>
            </div>
          )}

          {step === 1 && (
            <div className="card p-6 space-y-4">
              <h3 className="font-semibold text-gray-900">{t('domains.wizard.connectTitle')}</h3>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('domains.wizard.serverLabel')}</label>
                  <input type="text" value={wiz.server} onChange={(e) => setWiz({ ...wiz, server: e.target.value })} placeholder={t('domains.wizard.serverPlaceholder')} className="input w-full" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('domains.wizard.portLabel')}</label>
                  <input type="number" value={wiz.port} onChange={(e) => setWiz({ ...wiz, port: parseInt(e.target.value) })} className="input w-full" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="wizSsl" checked={wiz.useSsl} onChange={(e) => setWiz({ ...wiz, useSsl: e.target.checked })} className="checkbox" />
                <label htmlFor="wizSsl" className="text-sm text-gray-700">{t('domains.wizard.useSsl')}</label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('domains.wizard.adminUserLabel')}</label>
                <input type="text" value={wiz.adminUsername} onChange={(e) => setWiz({ ...wiz, adminUsername: e.target.value })} placeholder={t('domains.wizard.adminUserPlaceholder')} className="input w-full" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('domains.wizard.adminPasswordLabel')}</label>
                <div className="relative">
                  <input
                    type={showPwd ? 'text' : 'password'}
                    value={wiz.adminPassword}
                    onChange={(e) => setWiz({ ...wiz, adminPassword: e.target.value })}
                    className="input w-full pr-10"
                  />
                  <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {discoverError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 text-sm rounded-lg">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {discoverError}
                </div>
              )}

              <div className="flex gap-3">
                <button onClick={() => setStep(0)} className="btn btn-secondary">{t('common.buttons.back')}</button>
                <button
                  onClick={handleDiscover}
                  disabled={discovering || !wiz.server || !wiz.adminUsername || !wiz.adminPassword}
                  className="btn btn-primary flex items-center gap-2"
                >
                  {discovering ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  {t('domains.wizard.discoverButton')}
                </button>
              </div>
            </div>
          )}

          {step === 2 && discovered && (
            <div className="card p-6 space-y-5">
              <h3 className="font-semibold text-gray-900">{t('domains.wizard.discoveryTitle')}</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-green-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500">{t('domains.wizard.baseDn')}</div>
                  <div className="font-mono font-medium text-gray-800">{discovered.baseDn}</div>
                </div>
                <div className="bg-green-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500">{t('domains.wizard.dnsHostname')}</div>
                  <div className="font-mono font-medium text-gray-800">{discovered.dnsHostName}</div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('domains.wizard.displayNameLabel')}</label>
                <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} className="input w-full" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('domains.wizard.groupBaseLabel')}</label>
                <div className="border border-gray-200 rounded-xl p-3 max-h-64 overflow-y-auto bg-gray-50">
                  <div
                    className={clsx(
                      'flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer text-sm mb-1',
                      selectedOu === discovered.baseDn ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-100 text-gray-700'
                    )}
                    onClick={() => setSelectedOu(discovered.baseDn)}
                  >
                    <FolderOpen className="w-4 h-4 text-yellow-500" />
                    <span className="font-medium">{t('domains.wizard.rootDomain')}</span>
                    <span className="text-xs text-gray-400 ml-1 font-mono">{discovered.baseDn}</span>
                    {selectedOu === discovered.baseDn && <CheckCircle2 className="w-3.5 h-3.5 text-blue-600 ml-auto" />}
                  </div>
                  {discovered.ouTree?.map((node) => (
                    <OuNode key={node.dn} node={node} selected={selectedOu} onSelect={setSelectedOu} depth={1} />
                  ))}
                </div>
                {selectedOu && (
                  <div className="mt-2 text-xs text-gray-500 font-mono">{selectedOu}</div>
                )}
              </div>

              <div className="flex gap-3">
                <button onClick={() => setStep(1)} className="btn btn-secondary">{t('common.buttons.back')}</button>
                <button onClick={() => setStep(3)} disabled={!displayName} className="btn btn-primary">{t('common.buttons.next')}</button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="card p-6 space-y-5">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-600" />
                <h3 className="font-semibold text-gray-900">{t('domains.wizard.serviceTitle')}</h3>
              </div>
              <p className="text-sm text-gray-600">
                <Trans
                  i18nKey="domains.wizard.serviceHint"
                  components={{ mono: <span className="font-mono font-medium" />, b: <strong /> }}
                />
              </p>

              {!svcResult && !svcError && (
                <button
                  onClick={handleProvisionServiceAccount}
                  disabled={provisioningAccount}
                  className="btn btn-primary flex items-center gap-2"
                >
                  {provisioningAccount
                    ? <><RefreshCw className="w-4 h-4 animate-spin" /> {t('domains.wizard.serviceProvisioning')}</>
                    : <><Shield className="w-4 h-4" /> {t('domains.wizard.serviceProvisionButton')}</>}
                </button>
              )}

              {svcError && (
                <div className="space-y-3">
                  <div className="flex items-start gap-2 p-3 bg-red-50 text-red-700 text-sm rounded-lg">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{svcError}</span>
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => { setSvcError(null); setSvcResult(null) }} className="btn btn-secondary text-sm">{t('common.buttons.retry')}</button>
                    <button onClick={() => setStep(4)} className="btn btn-secondary text-sm text-orange-700 border-orange-200 bg-orange-50">{t('domains.wizard.skipAdvanced')}</button>
                  </div>
                </div>
              )}

              {svcResult && (
                <div className="space-y-4">
                  <div className={clsx(
                    'flex items-start gap-2 p-3 text-sm rounded-lg',
                    svcResult.passwordSet ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-800'
                  )}>
                    {svcResult.passwordSet
                      ? <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      : <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />}
                    <div>
                      {svcResult.passwordSet
                        ? <><strong>{t('domains.wizard.serviceSuccess')}</strong> {t('domains.wizard.serviceBindDn')}: <span className="font-mono">{svcResult.serviceAccountDn}</span></>
                        : <><strong>{t('domains.wizard.servicePartial')}</strong> {svcResult.errorMessage}</>}
                    </div>
                  </div>

                  {svcResult.delegationScript && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-700">
                          {svcResult.passwordRequiresManualSet
                            ? t('domains.wizard.scriptSetPassword')
                            : t('domains.wizard.scriptDelegate')}
                        </p>
                        <button
                          onClick={() => handleCopyScript(svcResult.delegationScript)}
                          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                        >
                          {scriptCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                          {scriptCopied ? t('domains.wizard.copied') : t('domains.wizard.copy')}
                        </button>
                      </div>
                      <pre className="text-xs font-mono bg-gray-900 text-green-300 p-4 rounded-xl overflow-x-auto max-h-48 whitespace-pre-wrap">
                        {svcResult.delegationScript}
                      </pre>
                    </div>
                  )}

                  <div className="flex gap-3 pt-2">
                    <button onClick={() => setStep(2)} className="btn btn-secondary">{t('common.buttons.back')}</button>
                    <button onClick={() => setStep(4)} disabled={!displayName} className="btn btn-primary">{t('common.buttons.next')}</button>
                  </div>
                </div>
              )}

              {!svcResult && !svcError && (
                <button onClick={() => setStep(2)} className="btn btn-secondary">{t('common.buttons.back')}</button>
              )}
            </div>
          )}

          {step === 4 && (
            <div className="card p-6 space-y-4">
              <h3 className="font-semibold text-gray-900">{t('domains.wizard.confirmTitle')}</h3>
              <div className="space-y-2 text-sm">
                {[
                  [t('domains.wizard.summaryName'),    displayName],
                  [t('domains.wizard.summaryServer'),  `${wiz.server}:${wiz.port}${wiz.useSsl ? ' (SSL)' : ''}`],
                  [t('domains.wizard.summaryBaseDn'), discovered?.baseDn],
                  [t('domains.wizard.summaryDns'),    discovered?.dnsHostName],
                  [t('domains.wizard.summaryGroups'), selectedOu],
                  [t('domains.wizard.summaryService'),svcResult?.serviceAccountDn ?? wiz.adminUsername],
                ].map(([k, v]) => (
                  <div key={k} className="flex gap-3">
                    <span className="text-gray-500 w-32 flex-shrink-0">{k}</span>
                    <span className="font-mono text-gray-800 break-all">{v}</span>
                  </div>
                ))}
              </div>
              {!svcResult?.passwordSet && (
                <div className="flex items-start gap-2 p-3 bg-yellow-50 text-yellow-800 text-sm rounded-lg">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{t('domains.wizard.warningNoPassword')}</span>
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button onClick={() => setStep(3)} className="btn btn-secondary">{t('common.buttons.back')}</button>
                <button
                  onClick={handleCreateWizard}
                  disabled={saving}
                  className="btn btn-primary flex items-center gap-2"
                >
                  {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  {t('domains.wizard.createButton')}
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="card p-6">
          <h3 className="font-semibold text-gray-900 mb-4">{t('domains.wizard.manualTitle')}</h3>
          <form onSubmit={handleCreateAdvanced} className="space-y-4">
            {[
              { key: 'displayName',      label: t('domains.wizard.fieldDisplayName'), required: true  },
              { key: 'server',           label: t('domains.wizard.fieldServer'),      required: true  },
              { key: 'baseDn',           label: t('domains.wizard.fieldBaseDn'),      required: true  },
              { key: 'bindDn',           label: t('domains.wizard.fieldBindDn'),      required: true  },
              { key: 'userSearchFilter', label: t('domains.wizard.fieldUserFilter'),  required: false },
              { key: 'groupSearchBase',  label: t('domains.wizard.fieldGroupBase'),   required: false },
            ].map(({ key, label, required }) => (
              <div key={key}>
                <label className="block text-sm font-medium text-gray-700 mb-1">{label}{required && ' *'}</label>
                <input
                  type="text"
                  value={adv[key] ?? ''}
                  onChange={(e) => setAdv({ ...adv, [key]: e.target.value })}
                  className="input w-full font-mono text-sm"
                  required={required}
                />
              </div>
            ))}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('domains.wizard.fieldPort')}</label>
                <input type="number" value={adv.port} onChange={(e) => setAdv({ ...adv, port: parseInt(e.target.value) })} className="input w-full" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('domains.wizard.fieldPassword')}</label>
                <input type="password" value={adv.bindPassword} onChange={(e) => setAdv({ ...adv, bindPassword: e.target.value })} className="input w-full" required />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input type="checkbox" checked={adv.useSsl} onChange={(e) => setAdv({ ...adv, useSsl: e.target.checked })} className="checkbox" />
                {t('domains.wizard.useSsl')}
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input type="checkbox" checked={adv.enabled} onChange={(e) => setAdv({ ...adv, enabled: e.target.checked })} className="checkbox" />
                {t('domains.wizard.enabled')}
              </label>
            </div>
            <div className="flex justify-end pt-2">
              <button type="submit" disabled={saving} className="btn btn-primary flex items-center gap-2">
                {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {t('domains.wizard.createButton')}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  )
}
